<script setup lang="ts">
import type { TableOrderInfo } from "@innoclouds/api";
import { useTranslation } from "i18next-vue";
import { computed, ref, watch } from "vue";
import BottomSheet from "#/ui/bottomSheet/BottomSheet.vue";
import ActiveOrdersList from "./activeOrdersList/ActiveOrdersList.vue";
import type { TableActiveOrdersStage, TableOrderIntent } from "./stages";
import TableActions from "./tableActions/TableActions.vue";

const props = defineProps<{
	modelValue: boolean;
	tableNumber: number | string;
	orders: TableOrderInfo[];
}>();

const emit = defineEmits<{
	"update:modelValue": [value: boolean];
	"create-order": [];
	"view-order": [order: TableOrderInfo];
	"pay-order": [order: TableOrderInfo];
}>();

const { t } = useTranslation();

const stage = ref<TableActiveOrdersStage>("table-action");
const intent = ref<TableOrderIntent>("open");

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit("update:modelValue", value),
});

watch(
	() => props.modelValue,
	(value) => {
		if (!value) return;
		stage.value = "table-action";
		intent.value = "open";
	},
);

const headerTitle = computed(() => {
	if (stage.value === "list") return intent.value === "pay" ? t(`table-service.active-orders.title.pay`) : t(`table-service.active-orders.title.open`);
	return t(`table-service.table-action.title`, { number: props.tableNumber });
});

const headerSubtitle = computed(() => {
	if (stage.value === "list") return t(`table-service.active-orders.subtitle`);
	return t(`table-service.table-action.subtitle`, { count: props.orders.length });
});

const selectIntent = (value: TableOrderIntent) => {
	intent.value = value;
	stage.value = "list";
};

const createOrder = () => {
	emit("create-order");
	isOpen.value = false;
};

const selectOrder = (order: TableOrderInfo) => {
	if (intent.value === "pay") emit("pay-order", order);
	else emit("view-order", order);

	isOpen.value = false;
};

const goBack = () => {
	if (stage.value === "list") stage.value = "table-action";
};
</script>

<template>
	<BottomSheet v-model="isOpen" class="table_active_orders_sheet" teleport-to="#app">
		<template #header>
			<div class="table_active_orders_header">
				<div class="table_active_orders_header_text">
					<p class="table_active_orders_title">{{ headerTitle }}</p>
					<p class="table_active_orders_subtitle">{{ headerSubtitle }}</p>
				</div>
			</div>
		</template>

		<TableActions v-if="stage === 'table-action'" :payableCount="props.orders.length" @select="selectIntent" @create-order="createOrder" />
		<ActiveOrdersList v-else-if="stage === 'list'" :orders="props.orders" @select="selectOrder" @back="goBack" />
	</BottomSheet>
</template>

<style lang="scss">
  @use './tableActiveOrders.scss';
</style>
